import { useState } from "react";
import { Search, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd, fmtDate } from "@/lib/api";
import { useFetch, PageHeader, Panel, StatusBadge, Table, Empty, Loading, FilterTabs } from "@/components/common";
import { Thread, useAttachment } from "@/components/support";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = [["open", "Open"], ["in_progress", "In progress"], ["resolved", "Resolved"], ["closed", "Closed"]];

function TicketDetail({ id, onClose, onChanged }) {
  const { data: t, reload, setData } = useFetch(`/admin/tickets/${id}`);
  const img = useAttachment(`/admin/tickets/${id}/attachment`, !!t?.has_attachment);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const act = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
      await reload();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  if (!t) return null;
  const d = t.deposit;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-[#121722] border-[#242F42] text-white max-h-[92vh] overflow-y-auto" data-testid="admin-ticket-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-3"><span className="num">{t.ticket_code}</span><StatusBadge status={t.status} testid="admin-ticket-status" /></DialogTitle></DialogHeader>
        <div className="grid gap-5 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[["User ID", t.user_code], ["Email", t.email], ["Amount", t.amount ? usd(t.amount) : "—"], ["Method", t.method_label || "—"],
                ["UTR / Txn ID", t.txn_ref || "—"], ["Category", t.category_label], ["Created", fmtDate(t.created_at)], ["Updated", fmtDate(t.updated_at)]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-[#1E2738] bg-[#0B0E14] p-2.5"><p className="text-[11px] text-slate-500">{l}</p><p className="num break-all text-white">{v}</p></div>
              ))}
            </div>
            {d && (
              <div className="rounded-lg border border-[#1E2738] bg-[#0B0E14] p-3 text-xs" data-testid="admin-ticket-linked-deposit">
                <p className="text-slate-500">Linked deposit</p>
                <p className="mt-1 text-white"><span className="num">{usd(d.amount)}</span> · {d.method_label} · <span className="num">{d.txn_id || "no txn"}</span></p>
                <div className="mt-1"><StatusBadge status={d.status} /></div>
              </div>
            )}
            {t.has_attachment && (
              img ? <a href={img} target="_blank" rel="noreferrer"><img src={img} alt="Ticket screenshot" className="max-h-64 w-full rounded-lg border border-[#1E2738] object-contain" data-testid="admin-ticket-attachment" /></a>
                : <p className="flex items-center gap-2 text-xs text-slate-500"><ImageIcon size={14} />{img === false ? "Could not load screenshot" : "Loading screenshot…"}</p>
            )}
            <div>
              <label className="nx-label">Update status</label>
              <select className="nx-input" value={t.status} disabled={busy === "status"}
                onChange={(e) => act("status", async () => { const r = await api.patch(`/admin/tickets/${id}`, { status: e.target.value }); setData(r.data); toast.success("Status updated"); })}
                data-testid="admin-ticket-status-select">
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-4 lg:col-span-3">
            <Thread messages={t.messages} />
            <form onSubmit={(e) => { e.preventDefault(); act("reply", async () => { await api.post(`/admin/tickets/${id}/reply`, { text: reply }); setReply(""); toast.success("Reply sent to user"); }); }} className="space-y-2">
              <label className="nx-label">Reply to user (visible to customer)</label>
              <textarea required rows={3} className="nx-input" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-ticket-reply-input" />
              <button className="nx-btn-primary" disabled={busy === "reply"} data-testid="admin-ticket-reply-btn">{busy === "reply" && <Loader2 size={14} className="animate-spin" />}Send reply</button>
            </form>
            <div className="rounded-xl border border-[#1E2738] p-4">
              <p className="nx-label">Internal notes (admin only)</p>
              <div className="mb-3 space-y-2" data-testid="admin-ticket-notes">
                {!t.admin_notes?.length && <p className="text-xs text-slate-500">No notes yet</p>}
                {t.admin_notes?.map((n) => <div key={n.id} className="rounded-lg bg-[#0B0E14] px-3 py-2 text-xs"><p className="text-slate-500">{n.by} · {fmtDate(n.at)}</p><p className="mt-1 whitespace-pre-wrap text-slate-200">{n.text}</p></div>)}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); act("note", async () => { await api.post(`/admin/tickets/${id}/note`, { text: note }); setNote(""); toast.success("Note added"); }); }} className="flex gap-2">
                <input required className="nx-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note" data-testid="admin-ticket-note-input" />
                <button className="nx-btn-secondary" disabled={busy === "note"} data-testid="admin-ticket-note-btn">Add</button>
              </form>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTickets() {
  const [status, setStatus] = useState("unresolved");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const { data, loading, reload } = useFetch(`/admin/tickets?status=${status}&category=${category}&search=${encodeURIComponent(search)}`);
  const { data: cats } = useFetch("/tickets/categories");
  const [sel, setSel] = useState(null);
  const c = data?.counts || {};
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Admin" title="Support tickets" subtitle="Deposit help requests submitted by users." />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {STATUSES.map(([k, l]) => (
          <div key={k} className="nx-card p-4" data-testid={`ticket-count-${k}`}><p className="text-[11px] uppercase tracking-wider text-slate-400">{l}</p>
            <p className={`num mt-2 text-2xl font-semibold ${k === "open" ? "text-amber-400" : "text-white"}`}>{c[k] ?? 0}</p></div>
        ))}
      </div>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          <FilterTabs value={status} onChange={setStatus} options={[["unresolved", "Unresolved"], ...STATUSES, ["", "All"]]} testid="ticket-status-filter" />
          <select className="nx-input w-56" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ticket-category-filter">
            <option value="">All categories</option>
            {Object.entries(cats || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setSearch(q); }} className="flex gap-2">
          <div className="relative"><Search size={15} className="absolute left-3 top-3 text-slate-500" />
            <input className="nx-input w-72 pl-9" placeholder="Ticket ID, user ID, email, UTR" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ticket-search-input" /></div>
          <button className="nx-btn-secondary" data-testid="ticket-search-btn">Search</button>
        </form>
      </div>
      <Panel>
        {loading ? <Loading /> : (
          <Table head={["Created", "Ticket ID", "User ID", "Email", "Category", "Amount", "UTR / Txn", "Status", "Updated"]} testid="admin-tickets-table">
            {!data?.tickets?.length && <Empty cols={9} text="No tickets" />}
            {data?.tickets?.map((t) => (
              <tr key={t.id} onClick={() => setSel(t.id)} className="cursor-pointer hover:bg-[#1A2232]" data-testid={`admin-ticket-row-${t.ticket_code}`}>
                <td className="nx-td text-xs">{fmtDate(t.created_at)}</td>
                <td className="nx-td num text-[#10B981]">{t.ticket_code}</td>
                <td className="nx-td num">{t.user_code}</td>
                <td className="nx-td text-xs">{t.email}</td>
                <td className="nx-td">{t.category_label}</td>
                <td className="nx-td num">{t.amount ? usd(t.amount) : "—"}</td>
                <td className="nx-td num max-w-[160px] truncate text-xs">{t.txn_ref || "—"}</td>
                <td className="nx-td"><StatusBadge status={t.status} /></td>
                <td className="nx-td text-xs">{fmtDate(t.updated_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      {sel && <TicketDetail id={sel} onClose={() => setSel(null)} onChanged={reload} />}
    </div>
  );
}
